// components/DiagnosticosSection.tsx

import { useEffect, useMemo, useState } from 'react';
import { Trash2 } from 'lucide-react';

import {
  DataTableColumn,
} from '@/presentation/components/common/DataTable';
import { IconButton } from '@/presentation/components/common/IconButton/IconButton';
import { useCongrupo } from '@/presentation/hooks/useCongrupo';
import { useSimple } from '@/presentation/hooks/useSimple';

import { ActionSection } from './ActionSection';
import { CreateDialog } from './CreateActionDialog';
import { DeleteActionDialog } from './DeleteActionDialog';
import { Form } from '@/presentation/components/Forms/Form/Form';
import { useAuthContext } from '@/presentation';
import { formatSmartDateTime } from '@/shared/utils/dateFormaters';
import { formatGroup } from '@/shared';

interface ActionItem {
  id: number;
  nombre: string;
  usuario: string;
  fecha: Date;
}

interface DiagnosticosSectionProps {
  incidenteId: string;
  estadoId: number;
}

export function DiagnosticosSection({
  incidenteId,
  estadoId
}: DiagnosticosSectionProps) {
  const { user } = useAuthContext();
  const [createOpen, setCreateOpen] =
    useState(false);

  const [deleteOpen, setDeleteOpen] =
    useState(false);

  const [selectedId, setSelectedId] =
    useState<number | null>(null);

  const {
    data: tiposFalla,
    reload: loadTiposFalla,
  } = useSimple('luc_tipo_falla');

  const {
    remove,
  } = useSimple('luc_diagnosticos');

  const {
    data: diagnosticos,
    loadByGroup,
    create,
  } = useCongrupo();

  const loadDiagnosticos = async () => {
    await loadByGroup(
      'luc_diagnosticos',
      incidenteId
    );
  };

  useEffect(() => {
    loadDiagnosticos();
  }, [incidenteId]);

  const optionGroups = useMemo(() => {
    const grupos = new Map<
      number | undefined,
      { value: number; label: string }[]
    >();

    for (const falla of tiposFalla) {
      const opciones = grupos.get(falla.id_grupo) ?? [];

      opciones.push({
        value: falla.id,
        label: falla.nombre,
      });

      grupos.set(falla.id_grupo, opciones);
    }

    return Array.from(grupos, ([idGrupo, options]) => ({
      label: idGrupo == null ? 'Sin grupo' : formatGroup(idGrupo),
      options,
    }));
  }, [tiposFalla]);

  const columns: DataTableColumn<ActionItem>[] =
    [
      {
        key: 'nombre',
        header: 'Detalle',
      },
      {
        key: 'usuario',
        header: 'Usuario',
      },
      {
        key: 'fecha',
        header: 'Fecha',
        render: (item) =>
          formatSmartDateTime(item.fecha),
      },
      {
        key: 'eliminar',
        header: '',
        render: (item) => (
          <IconButton
            icon={<Trash2 size={30} />}
            title="Eliminar"
            color="danger"
            onClick={() => {
              setSelectedId(item.id);
              setDeleteOpen(true);
            }}
          />
        ),
      },
    ];

  const handleOpenCreate = async () => {
    if (estadoId == 7) {
      return
    }
    if (tiposFalla.length === 0) {
      await loadTiposFalla();
    }

    setCreateOpen(true);
  };

  const handleCreate = async (
    values: Record<string, unknown>
  ) => {
    await create('luc_diagnosticos', {
      nombre: String(values.nombre),
      grupo: incidenteId,
      usuario: String(user?.id),
      fecha: new Date()
    });

    setCreateOpen(false);

    // Actualiza solamente la tabla.
    await loadDiagnosticos();
  };

  const handleDelete = async () => {
    if (selectedId === null) return;

    await remove(selectedId);

    setDeleteOpen(false);
    setSelectedId(null);

    // No hace falta recargar toda la página.
    await loadDiagnosticos();
  };

  return (
    <>
      <ActionSection
        title="Diagnósticos"
        buttonLabel="Nueva Falla"
        data={diagnosticos}
        columns={columns}
        emptyMessage="No hay diagnósticos cargados"
        onCreate={handleOpenCreate}
      />

      <CreateDialog
        open={createOpen}
        title="Nueva Falla"
        onClose={() => setCreateOpen(false)}
      >
        <Form
          fields={[
            {
              name: "nombre",
              type: "select",
              label: "Falla",
              optionGroups,
            },
          ]}
          submitLabel="Agregar"
          onSubmit={handleCreate}
        />
      </CreateDialog>

      <DeleteActionDialog
        open={deleteOpen}
        title="Eliminar diagnóstico"
        message="¿Seguro que quiere eliminar la falla?"
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
      />
    </>
  );
}